import {
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';

import { ClickOptions } from '../../coaching-insight/click-options';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { composeTagIdentifierNext } from '../../coaching-tags/tags/tags.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { disuseStore, useDispatcher, useStore } from '../../store.decorator';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { TagGroupFormValue } from '../observation-tag-group/tag-group-form-value.model';

import { kpiSetReload } from './kpi-set.observation.action';
import { ColumnID } from './kpi-set.observation.model';
import kpiSetReducer from './kpi-set.observation.reducer';
import { KpiSetState } from './kpi-set.observation.state';

@Component({
    selector: '[cmpsObservationKpiSet]',
    templateUrl: './kpi-set.observation.component.html',
})
@TakeUntilDestroy()
export class KpiSetObservationComponent
    implements
        OnChanges,
        OnInit,
        OnDestroy,
        StateToProps<KpiSetState>,
        TakeUntilDestroy
{
    state: KpiSetState;
    @Input() storePathPrefix: string;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ColumnID = ColumnID;
    @Input() focus: TagGroupFormValue; // TODO variant type so that to not reference form-value type directly
    @Input() performerId: number;
    @Input() readonly: boolean;
    @Input() metrics: MetricItem[];
    @Output() removeMetricClick: EventEmitter<number /* metricId */>;
    @Output() addMetricClick: EventEmitter<void>;
    private _prevMetrics: string;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
    ) {
        this.removeMetricClick = new EventEmitter();
        this.addMetricClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins, @typescript-eslint/dot-notation
        if (changes.hasOwnProperty('focus') && !changes['focus'].firstChange) {
            const differs = this._differs();
            if (differs) {
                this._reload();
            }
        }
    }

    ngOnInit(): void {
        // need to access a specific key for state (array like)
        useStore(
            this,
            `${this.storePathPrefix}.kpiSet__${this.focus.trackingId}`,
            kpiSetReducer.kpiSet,
        );
    }

    ngOnDestroy(): void {
        disuseStore(this);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.removeMetricClick && this.removeMetricClick.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.addMetricClick && this.addMetricClick.complete();
    }

    propsToDispatch(): void {
        this._differs();
        this._reload();
    }

    /** @private */ _onRemoveMetricClick(metricId: number): void {
        this._ngZone.run(() => {
            this.resolveConfirmationModal(
                TranslationKeyMap._9711_ARE_YOU_SURE_YOU_WANT_TO_REMOVE_THIS_METRIC_,
                ClickOptions.DELETEKPI,
                metricId,
            );
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resolveConfirmationModal(
        message: string,
        clickOption: number,
        metricId: number,
    ) {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(message),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    switch (clickOption) {
                        case ClickOptions.DELETEKPI:
                            this.removeMetricClick.emit(metricId);
                            break;
                        default:
                            console.log('No valid click option');
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onAddMetricClick(): void {
        this.addMetricClick.emit();
    }

    private _differs(): boolean {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const next =
            !this.focus || !this.focus.metrics
                ? null
                : this.focus.metrics.reduce(
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      (p, c) => `${p}${c.metricId}-${c.isActiveGoal}`,
                      '',
                  );
        const diff = this._prevMetrics !== next;
        this._prevMetrics = next;
        return diff;
    }

    private _reload(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.focus && this.focus.metrics) {
            useDispatcher(
                this,
                kpiSetReload({
                    performerId: this.performerId,
                    behaviourId: this.focus.behaviourId,
                    subBehaviourId: this.focus.subBehaviourId,
                    metrics: this.focus.metrics,
                    metricsLookup: this.metrics,
                    metricId:
                        this._activatedRoute.snapshot.queryParams.metricId,
                }),
            );
        }
    }
}
